// Kosten der E-POST-Briefe – nur für den Super Admin im Control Center (Björn/Team sehen keine Kosten).
// Die API liefert keine Preise: gerechnet wird mit den Vertragspreisen aus app_settings 'epost_preise'.
// Abgerechnet wird, was die Post gedruckt hat (Status 4, Monat = Druckdatum) – wie die Abrechnung der Post.
import { supabase } from '../supabase/client';

export interface EpostPreise {
  /** Brief inkl. 1. Blatt, schwarz-weiß */
  grund: number;
  /** je weiteres Blatt */
  blatt: number;
  /** Aufpreis Farbe je Blatt */
  farbe: number;
  einwurf: number;
  einschreiben: number;
  rueckschein: number;
  premiumadress: number;
}

export const PREIS_FELDER: { key: keyof EpostPreise; label: string }[] = [
  { key: 'grund', label: 'Brief inkl. 1. Blatt (s/w)' },
  { key: 'blatt', label: 'je weiteres Blatt' },
  { key: 'farbe', label: 'Aufpreis Farbe je Blatt' },
  { key: 'einwurf', label: 'Einwurf-Einschreiben' },
  { key: 'einschreiben', label: 'Einschreiben' },
  { key: 'rueckschein', label: 'Einschreiben Rückschein' },
  { key: 'premiumadress', label: 'PREMIUMADRESS je Brief' },
];

export const LEERE_PREISE: EpostPreise = { grund: 0, blatt: 0, farbe: 0, einwurf: 0, einschreiben: 0, rueckschein: 0, premiumadress: 0 };
export const PREISE_KEY = 'epost_preise';

export async function ladePreise(): Promise<EpostPreise> {
  const { data, error } = await supabase.from('app_settings').select('value').eq('key', PREISE_KEY).maybeSingle();
  if (error) throw error;
  return { ...LEERE_PREISE, ...((data?.value ?? {}) as Partial<EpostPreise>) };
}

export async function speicherePreise(p: EpostPreise): Promise<void> {
  const { error } = await supabase.from('app_settings').upsert({ key: PREISE_KEY, value: p, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}

export interface KostenBrief {
  status_id: number | null; test: boolean; seiten: number | null; einschreiben: string | null; created_at: string; gedruckt_at: string | null;
  optionen: { farbe?: boolean; duplex?: boolean; premiumAdress?: boolean } | null;
  objekt: { objektnummer: string | null; bezeichnung: string | null } | null;
}

/** Blätter aus Seiten (beidseitig: 2 Seiten je Blatt); ohne Seitenzahl 1 Blatt. */
export const blaetter = (b: Pick<KostenBrief, 'seiten' | 'optionen'>) =>
  Math.max(1, b.optionen?.duplex ? Math.ceil((b.seiten ?? 1) / 2) : (b.seiten ?? 1));

/** Kosten eines Briefs nach Vertragspreisen (auf Cent gerundet). */
export function briefKosten(b: KostenBrief, p: EpostPreise): number {
  const n = blaetter(b);
  let k = p.grund + (n - 1) * p.blatt;
  if (b.optionen?.farbe) k += n * p.farbe;
  if (b.einschreiben === 'Einwurf Einschreiben') k += p.einwurf;
  else if (b.einschreiben === 'Einschreiben') k += p.einschreiben;
  else if (b.einschreiben === 'Einschreiben Rückschein') k += p.rueckschein;
  if (b.optionen?.premiumAdress && !b.einschreiben) k += p.premiumadress;
  return Math.round(k * 100) / 100;
}

export interface KostenMonat { monat: string; briefe: number; blaetter: number; summe: number; nachObjekt: { objekt: string; briefe: number; summe: number }[] }

/** Gedruckte echte Briefe je Druckmonat; dazu `offen` = eingeliefert, noch nicht gedruckt (voraussichtliche Kosten). */
export function kostenUebersicht(briefe: KostenBrief[], p: EpostPreise): { monate: KostenMonat[]; offen: { briefe: number; summe: number } } {
  const monate = new Map<string, { briefe: number; blaetter: number; summe: number; obj: Map<string, { briefe: number; summe: number }> }>();
  const offen = { briefe: 0, summe: 0 };
  for (const b of briefe) {
    if (b.test || b.status_id === 99 || b.status_id === -1) continue;
    const k = briefKosten(b, p);
    if (b.status_id !== 4) { offen.briefe++; offen.summe = Math.round((offen.summe + k) * 100) / 100; continue; }
    const monat = (b.gedruckt_at ?? b.created_at).slice(0, 7);
    const m = monate.get(monat) ?? { briefe: 0, blaetter: 0, summe: 0, obj: new Map() };
    m.briefe++; m.blaetter += blaetter(b); m.summe = Math.round((m.summe + k) * 100) / 100;
    const o = b.objekt?.objektnummer ? `${b.objekt.objektnummer}${b.objekt.bezeichnung ? ` ${b.objekt.bezeichnung}` : ''}` : 'Ohne Objekt';
    const ob = m.obj.get(o) ?? { briefe: 0, summe: 0 };
    ob.briefe++; ob.summe = Math.round((ob.summe + k) * 100) / 100;
    m.obj.set(o, ob); monate.set(monat, m);
  }
  return {
    offen,
    monate: [...monate.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([monat, m]) => ({
      monat, briefe: m.briefe, blaetter: m.blaetter, summe: m.summe,
      nachObjekt: [...m.obj.entries()].map(([objekt, v]) => ({ objekt, ...v })).sort((a, b) => b.summe - a.summe),
    })),
  };
}

export const euro = (n: number) => n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
